import React, { useState, useEffect } from "react";
import { createNft, mplTokenMetadata } from "@metaplex-foundation/mpl-token-metadata";
import {
    createGenericFile,
    signerIdentity,
    generateSigner,
    percentAmount,
} from "@metaplex-foundation/umi";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { bundlrUploader } from "@metaplex-foundation/umi-uploader-bundlr";
import { useWallet } from "@solana/wallet-adapter-react";
import { base58 } from "@metaplex-foundation/umi/serializers";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";

const CreateNft = () => {
    const wallet = useWallet();
    const [status, setStatus] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        externalUrl: "",
        attributes: [{ trait_type: "", value: "" }],
        imageFile: null,
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData((prev) => ({ ...prev, imageFile: file }));
        if (file) {
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setPreviewUrl(null);
        }
    };

    // Cleanup preview URL when component unmounts or when a new file is selected.
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleAddAttribute = () => {
        setFormData((prev) => ({
            ...prev,
            attributes: [...prev.attributes, { trait_type: "", value: "" }],
        }));
    };

    const handleAttributeChange = (index, field, value) => {
        const updatedAttributes = [...formData.attributes];
        updatedAttributes[index][field] = value;
        setFormData((prev) => ({ ...prev, attributes: updatedAttributes }));
    };

    const handleCreateNft = async () => {
        try {
            if (!wallet.connected || !wallet.publicKey) {
                throw new Error("Wallet not connected");
            }
            if (!formData.imageFile) {
                throw new Error("Please upload an image file");
            }

            setStatus("Setting up Umi...");
            // Initialize Umi with your RPC endpoint and configure Bundlr uploader.
            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT)
                .use(mplTokenMetadata())
                .use(
                    bundlrUploader("https://node1.bundlr.network", {
                        identity: signerIdentity(wallet),
                    })
                )
                .use(walletAdapterIdentity(wallet));

            // Upload the image to Arweave.
            setStatus("Uploading image to Arweave...");
            const umiImageFile = createGenericFile(
                await formData.imageFile.arrayBuffer(),
                formData.imageFile.name,
                {
                    tags: [{ name: "Content-Type", value: formData.imageFile.type }],
                }
            );
            const imageUri = await umi.uploader.upload([umiImageFile]);

            // Upload the metadata to Arweave.
            setStatus("Uploading metadata to Arweave...");
            const metadata = {
                name: formData.name,
                description: formData.description,
                image: imageUri[0],
                external_url: formData.externalUrl,
                attributes: formData.attributes,
                properties: {
                    files: [
                        {
                            uri: imageUri[0],
                            type: formData.imageFile.type,
                        },
                    ],
                    category: "image",
                },
            };
            const metadataUri = await umi.uploader.uploadJson(metadata);

            // Create the NFT using the reference pattern.
            setStatus("Creating the NFT...");
            const mint = generateSigner(umi); // Generate a new mint keypair for the NFT

            const tx = await createNft(umi, {
                mint,
                name: metadata.name,
                uri: metadataUri,
                sellerFeeBasisPoints: percentAmount(5.5),
            }).sendAndConfirm(umi);

            // Extract the signature (or use tx.signature if available)
            const signature = tx.signature ? tx.signature : "unknown";

            setStatus(
                `NFT Created!\n\nView Transaction: https://explorer.solana.com/tx/${signature}?cluster=mainnet-beta\n\nView NFT: https://explorer.solana.com/address/${mint.publicKey.toBase58()}?cluster=mainnet-beta`
            );
        } catch (error) {
            console.error(error);
            setStatus(`Error: ${error.message}`);
        }
    };

    return (
        <div>
            <h1>Create NFT</h1>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateNft();
                }}
            >
                <div>
                    <h1>Enter the data for your collectible</h1>
                    <label>
                        Name:
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
                    </label>
                </div>
                <div>
                    <label>
                        Description:
                        <textarea name="description" value={formData.description} onChange={handleInputChange} required />
                    </label>
                </div>
                <div>
                    <label>
                        External URL:
                        <input type="url" name="externalUrl" value={formData.externalUrl} onChange={handleInputChange} />
                    </label>
                </div>
                <div>
                    <label>
                        Image:
                        <input type="file" onChange={handleFileChange} accept="image/*" required />
                    </label>
                </div>
                {/* Display the image preview if available */}
                {previewUrl && (
                    <div>
                        <p>Image Preview:</p>
                        <img src={previewUrl} alt="Preview" style={{ maxWidth: "300px", marginTop: "10px" }} />
                    </div>
                )}
                <div>
                    <h3>Attributes</h3>
                    {formData.attributes.map((attribute, index) => (
                        <div key={index}>
                            <input
                                type="text"
                                placeholder="Trait Type"
                                value={attribute.trait_type}
                                onChange={(e) => handleAttributeChange(index, "trait_type", e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Value"
                                value={attribute.value}
                                onChange={(e) => handleAttributeChange(index, "value", e.target.value)}
                                required
                            />
                        </div>
                    ))}
                    <div className="mint-form-button-container">
                        <button className="mint-form-button" type="button" onClick={handleAddAttribute}>
                            Add Attribute
                        </button>
                    </div>
                </div>
                <div className="mint-form-button-container">
                    <button className="mint-form-button" type="submit" disabled={!wallet.connected}>
                        Create NFT
                    </button>
                </div>
            </form>
            {status && <p>{status}</p>}
        </div>
    );
};

export default CreateNft;
